import type {
  MarkPaidDto,
  TenantMonthlyPayment,
  WaiveDto,
} from '../../api/payments.service';
import { memberFullName, type DemoPaymentRecord, type DemoStoreData } from '../store-types';
import { route, type HandlerEntry } from '../handler-registry';

function toResponse(
  record: DemoPaymentRecord,
  data: DemoStoreData,
): TenantMonthlyPayment {
  const member = data.members.find((m) => m.id === record.studentTenantAccountId);
  const enrollment = data.enrollments.find(
    (e) => e.studentTenantAccountId === record.studentTenantAccountId,
  );
  const group = enrollment
    ? data.groups.find((g) => g.groupId === enrollment.groupId)
    : null;
  const feeBreakdown = group
    ? [
        {
          groupId: group.groupId,
          groupName: group.name,
          amount: record.amountDue,
          isOverride: enrollment?.monthlyFeeOverride !== null,
        },
      ]
    : [];
  const now = new Date();
  const isCurrentOrFuture =
    record.year > now.getFullYear() ||
    (record.year === now.getFullYear() && record.month >= now.getMonth() + 1);
  return {
    paymentId: record.paymentId,
    tenantId: data.tenant.tenantId,
    studentTenantAccountId: record.studentTenantAccountId,
    studentFullName: member ? memberFullName(member) : 'Sportiv necunoscut',
    year: record.year,
    month: record.month,
    status: record.status,
    amountDue: record.amountDue,
    amountPaid: record.amountPaid,
    feeBreakdown,
    paidAt: record.paidAt,
    paidMethod: record.paidMethod,
    paidByTenantAccountId: record.paidAt ? data.currentMemberId : null,
    note: record.note,
    waivedReason: record.waivedReason,
    isLate: record.status === 'Unpaid' && !isCurrentOrFuture,
    proofs: [],
  };
}

function recordFor(
  data: DemoStoreData,
  studentId: string,
  year: number,
  month: number,
): DemoPaymentRecord {
  const existing = data.payments.find(
    (p) =>
      p.studentTenantAccountId === studentId && p.year === year && p.month === month,
  );
  if (existing) return existing;
  const enrollment = data.enrollments.find(
    (e) => e.studentTenantAccountId === studentId && e.status === 'Active',
  );
  const group = enrollment
    ? data.groups.find((g) => g.groupId === enrollment.groupId)
    : null;
  const fee =
    (enrollment?.monthlyFeeOverride ?? group?.monthlyFee ?? 0);
  return {
    paymentId: `pay-${year}-${month}-${studentId}`,
    studentTenantAccountId: studentId,
    year,
    month,
    status: 'Unpaid',
    amountDue: fee,
    amountPaid: null,
    paidAt: null,
    paidMethod: null,
    note: null,
    waivedReason: null,
  };
}

function upsert(data: DemoStoreData, next: DemoPaymentRecord): DemoStoreData {
  const existingIdx = data.payments.findIndex((p) => p.paymentId === next.paymentId);
  const payments =
    existingIdx >= 0
      ? data.payments.map((p, i) => (i === existingIdx ? next : p))
      : [...data.payments, next];
  return { ...data, payments };
}

export const paymentsHandlers: readonly HandlerEntry[] = [
  route('GET', /^\/api\/tenants\/([^/]+)\/payments$/, (ctx, data) => {
    const year = Number(ctx.query.get('year'));
    const month = Number(ctx.query.get('month'));
    const groupId = ctx.query.get('groupId');
    const status = ctx.query.get('status');
    const students = data.members
      .filter((m) => m.roles.includes('Student'))
      .filter((m) => {
        if (!groupId) return true;
        return data.enrollments.some(
          (e) =>
            e.studentTenantAccountId === m.id &&
            e.groupId === groupId &&
            e.status === 'Active',
        );
      });
    const records = students.map((m) => recordFor(data, m.id, year, month));
    const filtered = status ? records.filter((r) => r.status === status) : records;
    return filtered.map((r) => toResponse(r, data));
  }),
  route('GET', /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)$/, (ctx, data) => {
    const id = decodeURIComponent(ctx.match[2]);
    const record = data.payments.find((p) => p.paymentId === id);
    if (!record) return { error: { status: 404, message: 'Not found' } };
    return toResponse(record, data);
  }),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/mark-paid$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as MarkPaidDto;
      return {
        mutate: (data: DemoStoreData) => {
          const parsed = parsePaymentId(id);
          if (!parsed) return { next: data, result: { error: { status: 404, message: 'Invalid id' } } as never };
          const current = recordFor(data, parsed.studentId, parsed.year, parsed.month);
          const updated: DemoPaymentRecord = {
            ...current,
            status: 'Paid',
            amountPaid: body.amountPaid,
            paidAt: body.paidAt,
            paidMethod: body.method,
            note: body.note ?? null,
            waivedReason: null,
          };
          const next = upsert(data, updated);
          return { next, result: toResponse(updated, next) };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/unmark$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => {
          const record = data.payments.find((p) => p.paymentId === id);
          if (!record) return { next: data, result: { error: { status: 404, message: 'Not found' } } as never };
          const updated: DemoPaymentRecord = {
            ...record,
            status: 'Unpaid',
            amountPaid: null,
            paidAt: null,
            paidMethod: null,
            note: null,
          };
          const next = upsert(data, updated);
          return { next, result: toResponse(updated, next) };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/waive$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      const body = ctx.body as WaiveDto;
      return {
        mutate: (data: DemoStoreData) => {
          const parsed = parsePaymentId(id);
          if (!parsed) return { next: data, result: { error: { status: 404, message: 'Invalid id' } } as never };
          const current = recordFor(data, parsed.studentId, parsed.year, parsed.month);
          const updated: DemoPaymentRecord = {
            ...current,
            status: 'Waived',
            amountPaid: null,
            paidAt: null,
            paidMethod: null,
            waivedReason: body?.reason ?? null,
          };
          const next = upsert(data, updated);
          return { next, result: toResponse(updated, next) };
        },
      };
    },
  ),
  route(
    'POST',
    /^\/api\/tenants\/([^/]+)\/payments\/([^/]+)\/recalculate$/,
    (ctx) => {
      const id = decodeURIComponent(ctx.match[2]);
      return {
        mutate: (data: DemoStoreData) => {
          const record = data.payments.find((p) => p.paymentId === id);
          return record
            ? { next: data, result: toResponse(record, data) }
            : { next: data, result: { error: { status: 404, message: 'Not found' } } as never };
        },
      };
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/athletes\/([^/]+)\/payments$/,
    (ctx, data) => {
      const studentId = decodeURIComponent(ctx.match[2]);
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      return data.payments
        .filter((p) => p.studentTenantAccountId === studentId)
        .filter((p) => dateInRange(p, from, to))
        .map((p) => toResponse(p, data));
    },
  ),
  route(
    'GET',
    /^\/api\/tenants\/([^/]+)\/me\/payments$/,
    (ctx, data) => {
      const from = ctx.query.get('from');
      const to = ctx.query.get('to');
      return data.payments
        .filter((p) => p.studentTenantAccountId === data.currentMemberId)
        .filter((p) => dateInRange(p, from, to))
        .map((p) => toResponse(p, data));
    },
  ),
];

function parsePaymentId(id: string): { year: number; month: number; studentId: string } | null {
  const m = /^pay-(\d+)-(\d+)-(.+)$/.exec(id);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), studentId: m[3] };
}

function dateInRange(
  record: DemoPaymentRecord,
  from: string | null,
  to: string | null,
): boolean {
  const dateStr = `${record.year}-${String(record.month).padStart(2, '0')}-01`;
  if (from && dateStr < from) return false;
  if (to && dateStr > to) return false;
  return true;
}
